import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Labor } from '../interface/labor';
import { first, switchMap } from 'rxjs/operators';
import { LaborUtilities } from '../class/labor-utilities';
import { Observable, firstValueFrom } from 'rxjs';
import { ApiPaginatedQueryParams, ApiPaginatedResponse, PaginationService } from './pagination.service';
import { DataService } from './data.service';
import { DisableOrActive } from '../interface/disableOrActive';
import { AutoCodeUtilities } from '../class/auto-code-utilities';
import { DocumentPipe } from '../shared/pipes/document.pipe';

@Injectable({
  providedIn: 'root'
})
export class LaborService {
  private _isFromMatrix = true;
  labors: Labor[] = [];

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private _dataService: DataService
  ) { }

  async get(id: string, cnpj?: string): Promise<Labor> {
    const url = `${environment.mkgoURL}/api/v1/labors/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj || this._isFromMatrix));
    const response: any = await this.http.get(url, header).pipe(first()).toPromise();
    if (!response.labors.length) {
      throw new Error(`Labor with id: ${id} has not found.`);
    }
    return response.labors[0];
  }

  async getAll(): Promise<Labor[]> {
    const url = `${environment.mkgoURL}/api/v1/labors`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const response: any = await this.http.get(url, header).pipe(first()).toPromise();
    this.labors = (response["labors"] as Labor[]).reverse();
    return this.labors;
  }

  getPaginated(apiParams: ApiPaginatedQueryParams, cnpj?: string): Observable<ApiPaginatedResponse<Labor>> {
    const url = `${environment.mkgoURL}/api/v1/labors/pages`;
    const params = PaginationService.getParams(apiParams);
    return this._dataService.httpOptions(cnpj || this._isFromMatrix, params).pipe(
      switchMap(options => this.http.get<ApiPaginatedResponse<Labor>>(url, options)),
      first()
    );
  }

  async getNextCode(): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/labors/next-code`;
    const header = await this._registeringCompanyOptions();
    const { code } = await firstValueFrom(this.http.get<{ code: string }>(url, header));
    return code;
  }

  async getNextCodeFromHistory(cnpj: string): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/labors/next-code/history`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const { code } = await firstValueFrom(this.http.get<{ code: string }>(url, header));
    return code;
  }

  async addOrUpdate(labor: Labor, options: { autoCode?: boolean } = {}): Promise<Labor> {
    const url = `${environment.mkgoURL}/api/v1/labors`;
    const apiLabor = LaborUtilities.complyAPI(labor);
    if (options.autoCode) {
      apiLabor.autoCode = true;
    }
    const header = options.autoCode
      ? await this._registeringCompanyOptions()
      : await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const post = () => firstValueFrom(this.http.post<Labor>(url, apiLabor, header));
    return options.autoCode ? AutoCodeUtilities.retryOnContention(post) : post();
  }

  private async _registeringCompanyOptions() {
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const cnpj = this.dataService.company?.cnpj;
    if (!cnpj) {
      return options;
    }
    return {
      ...options,
      headers: options.headers.append('Registering-Company-Cnpj', new DocumentPipe().transform(cnpj))
    };
  }

  async getInactives(): Promise<Labor[]> {
    const url = `${environment.mkgoURL}/api/v1/labors/inactives`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const response: any = await this.http.get(url, header).pipe(first()).toPromise();
    this.labors = (response["labors"] as Labor[]).reverse();
    return this.labors;
  }

  async inactiveOrActive(id: string, action: DisableOrActive): Promise<Labor> {
    const url = `${environment.mkgoURL}/api/v1/labors/${id}/${action}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this.http.put(url, {}, header));
    return resp;
  }

  async bulkUpdate(ids: string[], data: Partial<Labor> & { IRPJ?: any; INSS?: any; CSLL?: any }): Promise<{ updated: number }> {
    const url = `${environment.mkgoURL}/api/v1/labors/bulk-update`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await firstValueFrom(this.http.put<{ updated: number }>(url, { ids, data }, header));
    return resp;
  }
}
